import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import GOAL_LIST from '../../partials/goals/goal-list';
import GoalForm from '../../partials/goals/goal-form';

export default function Goals() {
  const { user } = useUser();
  const [allGoals, setAllGoals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');

  const getUserGoals = useCallback(async () => {
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_WEB_URL}api/goals/get-user-goals`, {}, { withCredentials: true });
      if (!Array.isArray(data.userGoals)) throw new Error('Unable to load your goals.');
      setAllGoals(data.userGoals);
      setError('');
    } catch (err) { setError(err.response?.data?.message || 'Unable to load your goals. Please try again.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.user_id) getUserGoals(); }, [user?.user_id, getUserGoals]);
  const userGoals = useMemo(() => allGoals.filter(goal =>
    goal.name.toLowerCase().includes(query.toLowerCase()) && (!filters.length || filters.includes(goal.status))
  ), [allGoals, query, filters]);

  const addGoal = async values => {
    const { data } = await axios.post(`${process.env.REACT_APP_WEB_URL}api/goals/add-one-goal`, values, { withCredentials: true });
    if (!data.success) throw new Error(data.message || 'Unable to add the goal.');
    await getUserGoals();
    toast.success('Goal added.', { position: 'top-right' });
  };
  const deleteGoal = async id => {
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_WEB_URL}api/goals/delete-one-goal`, { id }, { withCredentials: true });
      if (!data.success) throw new Error(data.message);
      await getUserGoals();
      toast.success('Goal deleted.', { position: 'top-right' });
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Unable to delete the goal.'); }
  };
  const moveGoal = async (id, direction) => {
    if (moving) return;
    setMoving(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_WEB_URL}api/goals/move-subgoal`, { id, direction }, { withCredentials: true });
      if (!data.success) throw new Error(data.message);
      await getUserGoals();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Unable to move the subgoal.'); }
    finally { setMoving(false); }
  };

  return <>
    <div className="manage-your-goals">
      <div className="goals-top-nav"><Link to="/" className="back-link">&lt; Back to Home</Link></div>
      <div className="goals-toolbar">
        <div className="search-container"><form className="search-form" onSubmit={event => { event.preventDefault(); setQuery(searchTerm); }}>
          <input className="search-input" aria-label="Search goals" placeholder="Search for a goal..." value={searchTerm} onChange={event => setSearchTerm(event.target.value)} />
          <button type="submit" className="search-button">Search</button>
        </form></div>
        <button className={`btn-outline pill ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(value => !value)}>Filters</button>
      </div>
      {showFilters && <div className="goals-filters-container"><div className="filter-group"><span className="filter-label">Status:</span>
        {[['not-started', 'Not Started'], ['in-progress', 'In Progress'], ['completed', 'Completed']].map(([status, label]) =>
          <button key={status} className={`filter-pill ${filters.includes(status) ? 'active' : ''}`} onClick={() => setFilters(previous => previous.includes(status) ? previous.filter(item => item !== status) : [...previous, status])}>{label}</button>
        )}
      </div></div>}
      {(query || filters.length > 0) && <p>Clear search and filters to reorder all subgoals. <button onClick={() => { setQuery(''); setSearchTerm(''); setFilters([]); }}>Clear</button></p>}
      {error && <p role="alert">{error} <button onClick={getUserGoals}>Try again</button></p>}
      <div className="row goals-main-content">
        <div className="col-md-6 col-12">{loading ? <p role="status">Loading goals…</p> : <GOAL_LIST userGoals={userGoals} allGoals={allGoals} deleteThisGoal={deleteGoal} getUserGoals={getUserGoals} moveGoal={moveGoal} reordering={moving} canReorder={!query && !filters.length} />}</div>
        <div className="col-md-6 col-12"><div className="add-goal-form"><h2>Add Goal</h2><GoalForm allGoals={allGoals} onSubmit={addGoal} submitLabel="Add Goal" /></div></div>
      </div>
    </div>
    <ToastContainer />
  </>;
}
